import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import {
  FaSave,
  FaArrowLeft,
  FaUser,
  FaCamera,
  FaUsers,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendar,
  FaIdCard,
  FaStickyNote,
} from 'react-icons/fa';
import {
  Alert,
  Spinner,
  Badge,
  Form,
  Button,
  InputGroup,
} from 'react-bootstrap';
import Select from 'react-select';

const getAuthToken = () => localStorage.getItem('authToken');

const CreateStudent = () => {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();
  const [formData, setFormData] = useState({
    application_no: '',
    date_of_application: new Date().toISOString().split('T')[0],
    admission_no: '',
    date_of_admission: '',
    id_card_no: '',
    name: '',
    address: '',
    gender: 'Male',
    dob: '',
    parents: '',
    mobile: '',
    batch: '',
    remark: '',
    parent_access_code: '',
    status: 'Active',
  });
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Fetch batches on mount
  useEffect(() => {
    fetchBatches();
  }, [selectedAcademicYear]);

  const fetchBatches = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setBatches([]);
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );

      if (response.data.status === 'success') {
        setBatches(response.data.data || []);
      } else {
        setAlert({
          show: true,
          message: 'Failed to load batches.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: 'Error fetching batches.',
        variant: 'danger',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken();

    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      selectedBatches.forEach((batchId, index) => {
        formDataToSend.append(`batch_ids[${index}]`, batchId);
      });

      if (photo) {
        formDataToSend.append('photo', photo);
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}create-student-v3`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.data.status === 'success') {
        const applicationNo = response.data.data.application_no;
        setAlert({
          show: true,
          message: `Student created successfully! Application No: ${applicationNo}`,
          variant: 'success',
        });

        if (window.confirm('Do you want to admit this student now?')) {
          await handleAdmit(applicationNo);
        }

        setTimeout(() => navigate(-1), 2000);
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Failed to create student.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Error creating student.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdmit = async (application_no) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}admit-student`,
        { application_no },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: `Student admitted successfully! Admission No: ${response.data.admission_no}`,
          variant: 'success',
        });
      } else {
        setAlert({
          show: true,
          message: response.data.message,
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Error admitting student.',
        variant: 'danger',
      });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaUser style={{ fontSize: '1.1rem' }} />
                Create Student
              </h4>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                variant="light"
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <FaSave size={14} /> {loading ? 'Saving...' : 'Save Student'}
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate(-1)}
                style={{
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <FaArrowLeft size={14} /> Back
              </Button>
            </div>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '16px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Form Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Creating student...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row g-4">
                {/* LEFT COLUMN */}
                <div className="col-12 col-lg-6">
                  <h5
                    style={{
                      color: '#667eea',
                      marginBottom: '20px',
                      fontWeight: '600',
                    }}
                  >
                    Personal Information
                  </h5>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Full Name <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaUser style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter full name"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Gender <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Date of Birth
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaCalendar style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Parents/Guardian
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaUsers style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="parents"
                        value={formData.parents}
                        onChange={handleChange}
                        placeholder="Parent or guardian name"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Mobile Number
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaPhone style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="Contact number(s)"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Address
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                          alignItems: 'flex-start',
                          paddingTop: '10px',
                        }}
                      >
                        <FaMapMarkerAlt style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Full address"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>
                </div>

                {/* RIGHT COLUMN */}
                <div className="col-12 col-lg-6">
                  <h5
                    style={{
                      color: '#667eea',
                      marginBottom: '20px',
                      fontWeight: '600',
                    }}
                  >
                    Academic & Administrative
                  </h5>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Date of Application
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaCalendar style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        name="date_of_application"
                        value={formData.date_of_application}
                        onChange={handleChange}
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      ID Card Number
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaIdCard style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="id_card_no"
                        value={formData.id_card_no}
                        onChange={handleChange}
                        placeholder="ID card number"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Batches
                    </Form.Label>
                    <Select
                      isMulti
                      options={batches.map((batch) => ({
                        value: String(batch.id),
                        label: `${batch.class_name} ${batch.division}`,
                      }))}
                      value={batches
                        .filter((b) => selectedBatches.includes(String(b.id)))
                        .map((b) => ({
                          value: String(b.id),
                          label: `${b.class_name} ${b.division}`,
                        }))}
                      onChange={(selectedOptions) =>
                        setSelectedBatches(
                          selectedOptions.map((opt) => opt.value),
                        )
                      }
                      classNamePrefix="react-select"
                      placeholder="Select one or more batches..."
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          borderColor: '#dee2e6',
                        }),
                      }}
                    />
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Upload Photo
                    </Form.Label>
                    <div
                      style={{
                        border: '2px dashed #dee2e6',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                      }}
                    >
                      {photoPreview ? (
                        <div>
                          <img
                            src={photoPreview}
                            alt="Preview"
                            style={{
                              width: '150px',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '15px',
                              border: '2px solid #667eea',
                            }}
                          />
                          <div>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setPhoto(null);
                                setPhotoPreview(null);
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = '';
                                }
                              }}
                              style={{ borderRadius: '6px' }}
                            >
                              Remove Photo
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <FaCamera
                            style={{
                              fontSize: '3rem',
                              color: '#6c757d',
                              marginBottom: '10px',
                            }}
                          />
                          <p style={{ margin: '10px 0 0 0', color: '#6c757d' }}>
                            Click to upload photo
                          </p>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                      />
                      {!photoPreview && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          style={{ marginTop: '10px', borderRadius: '6px' }}
                        >
                          Choose File
                        </Button>
                      )}
                    </div>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Remark
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                          alignItems: 'flex-start',
                          paddingTop: '10px',
                        }}
                      >
                        <FaStickyNote style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="remark"
                        value={formData.remark}
                        onChange={handleChange}
                        placeholder="Additional notes"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Status
                    </Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="Active">Active</option>
                      <option value="Suspend">Suspend</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '0' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Parent Access Code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="parent_access_code"
                      value={formData.parent_access_code}
                      readOnly
                      placeholder="Auto-generated"
                      style={{
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa',
                      }}
                    />
                    <Form.Text className="text-muted">
                      Will be generated automatically
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CreateStudent;
